import Dialog from "../../General/ConfirmableDialog";
import React, { useState, memo, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  Box,
  Checkbox,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L, { DivIcon } from "leaflet";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import RepairShopIncidentHistoryDialog from "./RepairShopIncidentHistoryDialog";
import {
  IncidentType,
  INCIDENT_TYPE_LABEL_MAP,
} from "../../../data/Fleet/IncidentType";
import { groupShopsByState } from "./repairShopStates";
import "./repairShopStateMarker.css";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface RepairShop {
  id: string;
  shopName: string;
  streetAddress: string;
  phoneNumber: string;
  latitude: number;
  longitude: number;
}

interface TruckLocationDto {
  truckId: string;
  unitNumber?: string;
  latitude?: number;
  longitude?: number;
}

const INDIVIDUAL_MARKERS_ZOOM = 7;
const EMPTY_SHOPS: RepairShop[] = [];

export const fetchRepairShops = async (
  token: string | null | undefined,
  incidentTypes: string[] = [],
) => {
  const params = new URLSearchParams();
  incidentTypes.forEach((incidentType) =>
    params.append("incidentTypes", incidentType),
  );

  const { data } = await axios.get<RepairShop[]>(
    `${apiVariable}/getAllRepairShops`,
    { headers: { Authorization: `Bearer ${token}` }, params },
  );

  return Array.from(new Map(data.map((shop) => [shop.id, shop])).values());
};

export const fetchTruckLocation = async (
  token: string | null | undefined,
  unitNumber: string,
) => {
  const { data } = await axios.get<TruckLocationDto>(
    `${apiVariable}/getTruckLocation/${unitNumber}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
};

const ShopMarkers = memo(
  ({
    shops,
    onEdit,
    onHistory,
  }: {
    shops: RepairShop[];
    onEdit: (shop: RepairShop) => void;
    onHistory: (shop: RepairShop) => void;
  }) => {
    const map = useMap();
    const [viewport, setViewport] = useState(() => ({
      zoom: map.getZoom(),
      bounds: map.getBounds(),
    }));
    const updateViewport = () => {
      const zoom = map.getZoom();
      const bounds = map.getBounds();
      setViewport((current) =>
        current.zoom === zoom && current.bounds.equals(bounds)
          ? current
          : { zoom, bounds },
      );
    };
    useMapEvents({ zoomend: updateViewport, moveend: updateViewport });

    const stateGroups = useMemo(() => groupShopsByState(shops), [shops]);
    const zoomedIn = viewport.zoom >= INDIVIDUAL_MARKERS_ZOOM;
    const visibleBounds = viewport.bounds.pad(0.25);
    const visibleShops = (zoomedIn ? shops : stateGroups.individual).filter(
      (shop) => visibleBounds.contains([shop.latitude, shop.longitude]),
    );

    return (
      <>
        {!zoomedIn &&
          stateGroups.grouped.map((group) => (
            <Marker
              key={`state-${group.name}`}
              position={group.position}
              icon={L.divIcon({
                className: "repair-shop-state-icon",
                html: `<span>${group.code}</span><strong>${group.shops.length}</strong>`,
                iconSize: [48, 48],
                iconAnchor: [24, 24],
              })}
              title={`${group.name}: ${group.shops.length} repair shops. Click to zoom in.`}
              eventHandlers={{
                click: () =>
                  map.setView(group.position, INDIVIDUAL_MARKERS_ZOOM),
              }}
            />
          ))}
        {visibleShops.map((s) => (
          <Marker key={s.id} position={[s.latitude, s.longitude]}>
            <Popup>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 0.5,
                  minWidth: { xs: 0, sm: 200 },
                }}
              >
                <strong>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${s.latitude},${s.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "underline" }}
                  >
                    {s.shopName}
                  </a>
                </strong>
                <Tooltip title="Edit coordinates">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onEdit(s);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                component="div"
                sx={{ m: 0, lineHeight: 1.2 }}
              >
                Street address: {s.streetAddress}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{ m: 0, mt: 0.25, lineHeight: 1.2 }}
              >
                Phone number: {s.phoneNumber}
              </Typography>
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => onHistory(s)}
                sx={{ mt: 1 }}
              >
                Incident history
              </Button>
            </Popup>
          </Marker>
        ))}
      </>
    );
  },
);

function ViewControl({
  shops,
  truck,
}: {
  shops: RepairShop[];
  truck?: TruckLocationDto;
}) {
  const map = useMap();
  useEffect(() => {
    if (truck?.latitude != null && truck.longitude != null) {
      map.setView([truck.latitude, truck.longitude], 12);
    } else if (shops.length) {
      const bounds = L.latLngBounds(
        shops.map((s) => [s.latitude, s.longitude] as [number, number]),
      );
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, shops, truck]);
  return null;
}

const RepairShopMap: React.FC = () => {
  const { token } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [incidentTypes, setIncidentTypes] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const [selectedShop, setSelectedShop] = useState<RepairShop | null>(null);
  const [historyShop, setHistoryShop] = useState<RepairShop | null>(null);
  const [latitudeValue, setLatitudeValue] = useState<string>("");
  const [longitudeValue, setLongitudeValue] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleOpenEdit = useCallback((shop: RepairShop) => {
    setSelectedShop(shop);
    setLatitudeValue(shop.latitude?.toString() ?? "");
    setLongitudeValue(shop.longitude?.toString() ?? "");
    setErrorMessage("");
  }, []);

  const handleCloseEdit = () => {
    setSelectedShop(null);
    setLatitudeValue("");
    setLongitudeValue("");
    setErrorMessage("");
  };

  const updateCoordinatesMutation = useMutation({
    mutationFn: async ({
      repairShopId,
      latitude,
      longitude,
    }: {
      repairShopId: string;
      latitude: number;
      longitude: number;
    }) => {
      const { data } = await axios.put(
        `${apiVariable}/repairShop/${repairShopId}/coordinates`,
        { latitude, longitude },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repairShops"] });
      handleCloseEdit();
    },
    onError: (error: any) => {
      setErrorMessage(
        error?.response?.data || "Failed to update repair shop coordinates",
      );
    },
  });

  const handleSaveCoordinates = () => {
    if (!selectedShop) {
      return;
    }

    const latitude = parseFloat(latitudeValue);
    const longitude = parseFloat(longitudeValue);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      setErrorMessage("Latitude and longitude must be valid numbers.");
      return;
    }

    if (latitude < -90 || latitude > 90) {
      setErrorMessage("Latitude must be between -90 and 90.");
      return;
    }

    if (longitude < -180 || longitude > 180) {
      setErrorMessage("Longitude must be between -180 and 180.");
      return;
    }

    setErrorMessage("");

    updateCoordinatesMutation.mutate({
      repairShopId: selectedShop.id,
      latitude,
      longitude,
    });
  };

  const {
    data: repairShops,
    isLoading: isShopsLoading,
    isError: isShopsError,
    error: shopsError,
  } = useQuery({
    queryKey: ["repairShops", incidentTypes],
    queryFn: () => fetchRepairShops(token, incidentTypes),
    enabled: !!token,
  });

  const {
    data: truckLocation,
    isLoading: isTruckLoading,
    isError: isTruckError,
    error: truckError,
  } = useQuery({
    queryKey: ["truckLocation", unitNumber],
    queryFn: () => fetchTruckLocation(token, unitNumber),
    enabled: !!token && !!unitNumber,
  });

  if (isShopsLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={400}
      >
        <CircularProgress />
      </Box>
    );
  if (isShopsError)
    return (
      <Typography color="error">
        Error loading shops: {(shopsError as any).message}
      </Typography>
    );

  const truckIcon = new DivIcon({
    html: `<div style="
      background:red;
      width:24px;height:24px;
      border-radius:50%;
      display:flex;
      align-items:center;
      justify-content:center;
      color:white;
      font-weight:bold;
      font-size:12px;
    ">T</div>`,
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  return (
    <Card
      sx={{ maxWidth: 1200, mx: "auto", my: 4, boxShadow: 3, borderRadius: 2 }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <TextField
            fullWidth
            label="Truck's Unit Number"
            variant="outlined"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && setUnitNumber(searchValue.trim())
            }
          />
          <FormControl fullWidth size="small">
            <InputLabel id="repair-shop-incident-type-label">
              Incident Type
            </InputLabel>
            <Select
              labelId="repair-shop-incident-type-label"
              id="repair-shop-incident-type-select"
              label="Incident Type"
              multiple
              value={incidentTypes}
              onChange={(event) =>
                setIncidentTypes(event.target.value as string[])
              }
              renderValue={(selected) =>
                (selected as string[])
                  .map((value) => INCIDENT_TYPE_LABEL_MAP[value] || value)
                  .join(", ")
              }
            >
              {IncidentType.filter(({ value }) => value).map(
                ({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={incidentTypes.includes(value)} />
                    <ListItemText primary={label} />
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          {incidentTypes.length > 0 && (
            <Button onClick={() => setIncidentTypes([])}>Clear</Button>
          )}
          {isTruckLoading && <CircularProgress size={24} />}
          {isTruckError && (
            <Typography color="error">{(truckError as any).message}</Typography>
          )}
        </Box>

        <Box
          sx={{ height: 800, borderRadius: "0 0 8px 8px", overflow: "hidden" }}
        >
          <MapContainer
            style={{ height: "100%", width: "100%" }}
            center={[39.8283, -98.5795]}
            zoom={4}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            <ShopMarkers
              shops={repairShops || EMPTY_SHOPS}
              onEdit={handleOpenEdit}
              onHistory={setHistoryShop}
            />

            {truckLocation?.latitude != null &&
              truckLocation.longitude != null && (
                <Marker
                  position={[truckLocation.latitude, truckLocation.longitude]}
                  icon={truckIcon}
                >
                  <Popup>
                    Truck: {truckLocation.unitNumber}
                    <br />
                    {truckLocation.latitude.toFixed(5)},{" "}
                    {truckLocation.longitude.toFixed(5)}
                  </Popup>
                </Marker>
              )}

            <ViewControl
              shops={repairShops || EMPTY_SHOPS}
              truck={truckLocation}
            />
          </MapContainer>
        </Box>
      </CardContent>
      <Dialog open={Boolean(selectedShop)} onClose={handleCloseEdit} fullWidth>
        <DialogTitle>Update Coordinates - {selectedShop?.shopName}</DialogTitle>
        <DialogContent dividers>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Latitude"
              type="number"
              value={latitudeValue}
              onChange={(event) => setLatitudeValue(event.target.value)}
              inputProps={{ step: "any" }}
              fullWidth
            />
            <TextField
              label="Longitude"
              type="number"
              value={longitudeValue}
              onChange={(event) => setLongitudeValue(event.target.value)}
              inputProps={{ step: "any" }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEdit}
            disabled={updateCoordinatesMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCoordinates}
            variant="contained"
            disabled={updateCoordinatesMutation.isPending}
          >
            {updateCoordinatesMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <RepairShopIncidentHistoryDialog
        shop={historyShop}
        onClose={() => setHistoryShop(null)}
      />
    </Card>
  );
};

export default RepairShopMap;
